import {
  RESAMPLE_BUTTON_ID,
  ResampleIntervals,
} from '@chart/types/resample.types';
import { Chip } from '@design/components/chip/Chip';
import { Dropdown, DropdownProps } from '@design/components/dropdown/Dropdown';
import { DropdownGenericItem } from '@design/components/dropdown/dropdown-list/DropdownList';
import {
  DropdownItem,
  DropdownListItem,
} from '@design/components/dropdown/dropdown-list-item/DropdownListItem';
import { MouseEvent, RefObject } from 'react';

import styles from './ResampleSelector.module.scss';

export interface ResampleSelectorLocales {
  [ResampleIntervals.OneMinute]: string;
  [ResampleIntervals.FiveMinutes]: string;
  [ResampleIntervals.FifteenMinutes]: string;
  [ResampleIntervals.ThirtyMinutes]: string;
  [ResampleIntervals.OneHour]: string;
  [ResampleIntervals.OneDay]: string;
  [ResampleIntervals.OneWeek]: string;
  [ResampleIntervals.OneMonth]: string;
}

export interface ResampleSelectorProps {
  selectedResample: ResampleIntervals;
  enabledResamples: ResampleIntervals[];
  onChange: (selectedResample: ResampleIntervals) => void;
  isResampleDropdownOpen: boolean;
  disableMenuBar: boolean;
  onItemsShowChange: DropdownProps<ResampleOption>['onItemsShowChange'];
  locales: ResampleSelectorLocales;
}

export interface ResampleOption extends DropdownGenericItem<DropdownItem> {
  type: ResampleIntervals;
}

export const ResampleSelector = ({
  selectedResample,
  onChange,
  enabledResamples,
  isResampleDropdownOpen = false,
  disableMenuBar,
  onItemsShowChange,
  locales,
}: ResampleSelectorProps) => {
  const items: ResampleOption[] = Object.values(ResampleIntervals).map(
    resampleInterval => ({
      type: resampleInterval,
      label: locales[resampleInterval],
      disabled: !enabledResamples.includes(resampleInterval),
    })
  );

  const onSelectItem = (item: ResampleOption) => {
    onChange(item.type);
  };

  const isClickedOutside = (
    ref: RefObject<HTMLDivElement | null>,
    e: MouseEvent
  ) => {
    const target = e.target as HTMLElement;
    return (
      !!ref.current &&
      target.id !== RESAMPLE_BUTTON_ID &&
      !ref.current.contains(target)
    );
  };

  const selectedResampleIndex = items.findIndex(
    item => item.type === selectedResample
  );

  return (
    <div
      className={styles.resampleSelectorRoot}
      data-testid="interval-selector"
    >
      <Dropdown
        dropdownItems={items}
        activeItemIdx={selectedResampleIndex}
        strategy="fixed"
        selectItem={onSelectItem}
        placement="bottom-start"
        shouldShowItems={isResampleDropdownOpen}
        shouldDisableItemMouseEvents={disableMenuBar}
        onItemsShowChange={onItemsShowChange}
        isClickedOutside={isClickedOutside}
        shouldSetActiveOnSelect={false}
        renderDropdownItem={(item, isActive, isFocused, isDisabled) => (
          <DropdownListItem
            label={item.label}
            supportiveText={item.supportiveText}
            isActive={isActive}
            isFocused={isFocused}
            isDisabled={isDisabled}
          />
        )}
      >
        <Chip
          className={styles.styledChip}
          type="choice"
          label={items[selectedResampleIndex].label}
          disabled={disableMenuBar}
          rightIcon="ChevronLightDown"
        />
      </Dropdown>
    </div>
  );
};
